// src/components/TrainingList.js
import React, { useState, useEffect } from 'react';
import { Card, Pagination, Modal, Spinner } from 'react-bootstrap';
import { CancelButton, DangerButton } from '../../Components/Buttons';
import { BsCheck2Square, BsSquare } from 'react-icons/bs';
import { marked } from 'marked';
import { getAppDisplayName, getAppColor } from './bvUtils';
import { useLanguage } from '../../Context/LanguageContext';
import './training.css';

const TrainingList = ({
  type,
  data,
  currentPage,
  onPageChange,
  editMode,
  selectedItems,
  onItemSelect,
  workspaceId,
  apiUrl,
  totalPages = 1, // NEW: Pagination metadata from parent (API)
  totalCount = 0  // NEW: Total count from parent (API)
}) => {
  const { t } = useLanguage();

  // NEW: Data is already paginated from API, no need to slice
  const itemsToShow = data;

  // Add state for content viewing modal
  const [showContentModal, setShowContentModal] = useState(false);
  const [contentData, setContentData] = useState({ title: '', content: '', type: '', created_at: '' });
  const [isContentLoading, setIsContentLoading] = useState(false);
  const [contentPage, setContentPage] = useState(1);
  const [contentPages, setContentPages] = useState([]);
  const CONTENT_CHARS_PER_PAGE = 5000; // Characters per page

  // Add state for tracking window width
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  // Add state for chatbots and apps
  const [chatbots, setChatbots] = useState([]);
  const [apps, setApps] = useState([]);

  // Add useEffect to track window resize
  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Fetch chatbots when component mounts
  useEffect(() => {
    const fetchChatbots = async () => {
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/chatbot/get-chatbots`,
          {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ workspace_id: workspaceId })
          }
        );
        if (response.ok) {
          const data = await response.json();
          setChatbots(data.chatbots || []);
        }
      } catch (error) {
      }
    };

    if (workspaceId) {
      fetchChatbots();
    }
  }, [workspaceId, apiUrl]);

  // Fetch apps when component mounts
  useEffect(() => {
    const fetchApps = async () => {
      try {
        const brandvoxToken = localStorage.getItem('brandvox_token');
        const response = await fetch(
          `${apiUrl}/api/v1/apps/get-apps`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${brandvoxToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.ok) {
          const data = await response.json();
          setApps(data.apps || []);
        }
      } catch (error) {
      }
    };

    if (apiUrl) {
      fetchApps();
    }
  }, [apiUrl]);

  // Add state for confirmation modal
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [confirmAction, setConfirmAction] = useState(null);

  // Function to break content into pages
  const getContentPages = (content) => {
    if (!content) return [];

    // If content is short enough, return as single page
    if (content.length <= CONTENT_CHARS_PER_PAGE) return [content];

    const pages = [];
    let i = 0;

    // Create pages of approximately equal length
    while (i < content.length) {
      pages.push(content.substring(i, i + CONTENT_CHARS_PER_PAGE));
      i += CONTENT_CHARS_PER_PAGE;
    }

    return pages;
  };

  // Function to fetch and display content
  const handleViewContent = async (contentTitle) => {
    if (editMode) return; // Don't fetch content in edit mode

    try {
      setIsContentLoading(true);
      setShowContentModal(true);
      setContentPage(1); // Reset to first page
      // Reset content data to avoid showing old title while loading new content
      setContentData({ title: '', content: '', type: '', created_at: '' });

      const brandvoxToken = localStorage.getItem('brandvox_token');
      const response = await fetch(
        `${apiUrl}/api/v1/train/get-content-of-trained-data?workspace_id=${workspaceId}&content_title=${encodeURIComponent(contentTitle)}`,
        {
          headers: { Authorization: `Bearer ${brandvoxToken}` },
        }
      );

      if (!response.ok) {
        throw new Error(t('dashboard.training.traininglist.errorFetchContent'));
      }

      const data = await response.json();
      setContentData({
        title: data.content_title,
        content: data.content,
        type: data.type,
        created_at: data.created_at
      });
      setContentPages(getContentPages(data.content));
    } catch (err) {
      alert(err.message);
    } finally {
      setIsContentLoading(false);
    }
  };

  // Get confirmation message
  const getConfirmMessage = () => {
    return t('dashboard.training.traininglist.confirmDeleteMessage');
  };

  // Handle confirmation
  const handleConfirm = async () => {
    setShowConfirmModal(false);

    if (confirmAction === 'deleteContent') {
      await handleConfirmDeleteContent();
    }
  };

  // Handle actual content deletion after confirmation
  const handleConfirmDeleteContent = async () => {
    try {
      const brandvoxToken = localStorage.getItem('brandvox_token');
      const body = JSON.stringify({
        workspace_id: workspaceId,
        content_names: [contentData.title],
      });

      const response = await fetch(`${apiUrl}/api/v1/train/delete-contents`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${brandvoxToken}`,
          'Content-Type': 'application/json',
        },
        body,
      });

      if (!response.ok) {
        throw new Error(t('dashboard.training.traininglist.errorDeleteContent'));
      }

      // Close the modal and refresh data
      setShowContentModal(false);
      onPageChange(type, 1); // Reset to first page
      window.location.reload(); // Refresh the page to update the data
    } catch (err) {
      alert(err.message);
    }
  };

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    return (
      <Pagination className='custom-pagination'>
        <Pagination.Prev
          disabled={currentPage === 1}
          onClick={() => onPageChange(type, currentPage - 1)}
        />
        {[...Array(totalPages).keys()].map((number) => (
          <Pagination.Item
            key={number + 1}
            active={number + 1 === currentPage}
            onClick={() => onPageChange(type, number + 1)}
          >
            {number + 1}
          </Pagination.Item>
        ))}
        <Pagination.Next
          disabled={currentPage === totalPages}
          onClick={() => onPageChange(type, currentPage + 1)}
        />
      </Pagination>
    );
  };

  return (
    <>
      {itemsToShow.map((item) => {
        const isSelected = selectedItems.includes(item.key);
        return (
          <Card
            key={item.key}
            className={`training-card ${editMode ? (isSelected ? 'selected' : 'unselected') : ''}`}
            onClick={() => {
              if (editMode) {
                onItemSelect(item.key);
              } else {
                handleViewContent(item.key);
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <Card.Body className="py-3">
              <div className="d-flex align-items-start">
                {editMode && (
                  <div className="select-indicator me-2 mt-1">
                    {isSelected ? <BsCheck2Square className="text-primary-custom" size={20} /> : <BsSquare className="text-muted" size={20} />}
                  </div>
                )}
                <div className="flex-grow-1" style={{ minWidth: 0, overflow: 'hidden' }}>
                  <Card.Title className="mb-2">
                    <strong>{item.key}</strong>
                  </Card.Title>
                  <div className="d-flex justify-content-between align-items-center">
                    <Card.Text className="mb-0">
                      <strong>
                        {item.size} {t('dashboard.training.traininglist.characters')}
                      </strong>
                    </Card.Text>
                    <Card.Text className="mb-0 text-muted small">{item.time}</Card.Text>
                  </div>
                  {/* Display assigned chatbots and apps */}
                  {item.assigned_to && (
                    (item.assigned_to.chatbots?.length > 0 || item.assigned_to.apps?.length > 0) && (
                      <div className="mt-2">
                        <small className="text-muted">
                          {t('dashboard.training.traininglist.assignedTo')}:
                        </small>
                        <div className="d-flex flex-wrap gap-1 mt-1">
                          {/* Display chatbots */}
                          {item.assigned_to.chatbots?.map(chatbotId => {
                            const chatbot = chatbots.find(c => c.chatbot_id === chatbotId);
                            return chatbot ? (
                              <span key={`chatbot-${chatbotId}`} className="badge assignment-badge-chatbot">
                                {t('dashboard.training.traininglist.supportBV')}: {chatbot.chatbot_name}
                              </span>
                            ) : null;
                          })}
                          {/* Display apps */}
                          {item.assigned_to.apps?.map(appId => {
                            const app = apps.find(a => a.app_id === appId);
                            if (!app) return null;
                            const appColor = getAppColor(app.name);
                            const appDisplayName = getAppDisplayName(app.name, t);
                            return (
                              <span
                                key={`app-${appId}`}
                                className="badge assignment-badge-app"
                                style={{ color: appColor, borderColor: appColor }}
                              >
                                {appDisplayName}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </Card.Body>
            {editMode && (
              <div className={`selection-overlay ${isSelected ? 'selected' : ''}`}></div>
            )}
          </Card>
        );
      })}
      {renderPagination()}

      {/* Content viewing modal */}
      <Modal
        show={showContentModal}
        onHide={() => setShowContentModal(false)}
        centered
        size="xl"
        className="modern-content-modal"
      >
        <Modal.Header className="modern-modal-header">
          <div className="modal-title-section">
            {isContentLoading ? (
              <div className="d-flex align-items-center">
                <Spinner animation="border" size="sm" className="me-3" />
                <span className="loading-text">{t('dashboard.training.traininglist.loadingContent')}</span>
              </div>
            ) : (
              <>
                <h4 className="modal-title-text">{contentData.title}</h4>
                <div className="modal-metadata">
                  {contentData.type && (
                    <span className="metadata-badge">
                      {contentData.type}
                    </span>
                  )}
                  {contentData.created_at && (
                    <span className="metadata-date">
                      {new Date(contentData.created_at + 'Z').toLocaleString()}
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={() => setShowContentModal(false)}
            aria-label="Close"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="m6 6 12 12M6 18 18 6" />
            </svg>
          </button>
        </Modal.Header>
        <Modal.Body className="modern-modal-body">
          {isContentLoading ? (
            <div className="loading-container">
              <div className="loading-content">
                <Spinner animation="border" className="loading-spinner" />
                <p className="loading-message">{t('dashboard.training.traininglist.loadingContent')}</p>
              </div>
            </div>
          ) : (
            <>
              <div className="content-wrapper">
                <style>{`
                  .content-display {
                    font-size: 0.875rem;
                    max-width: 100%;
                    line-height: 1.7;
                  }
                  .content-display p {
                    margin-bottom: 0.5rem;
                    line-height: 1.625;
                  }
                  .content-display p:last-child {
                    margin-bottom: 0;
                  }
                  .content-display br {
                    display: block;
                    margin-bottom: 0.375rem;
                  }
                  .content-display h1 {
                    font-size: 1.125rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h1:first-child {
                    margin-top: 0;
                  }
                  .content-display h2 {
                    font-size: 1rem;
                    font-weight: 700;
                    margin-bottom: 0.5rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h2:first-child {
                    margin-top: 0;
                  }
                  .content-display h3 {
                    font-size: 0.875rem;
                    font-weight: 700;
                    margin-bottom: 0.375rem;
                    margin-top: 0.5rem;
                  }
                  .content-display h3:first-child {
                    margin-top: 0;
                  }
                  .content-display ul {
                    list-style-type: disc;
                    list-style-position: inside;
                    margin-bottom: 0.75rem;
                    padding-left: 0;
                  }
                  .content-display ol {
                    list-style-type: decimal;
                    list-style-position: inside;
                    margin-bottom: 1rem;
                    padding-left: 0;
                  }
                  .content-display li {
                    margin-bottom: 0.25rem;
                  }
                  .content-display blockquote {
                    border-left: 4px solid rgba(107, 114, 128, 0.3);
                    padding-left: 1rem;
                    font-style: italic;
                    margin: 1rem 0;
                  }
                  .content-display code {
                    background-color: rgba(0, 0, 0, 0.05);
                    padding: 0.125rem 0.375rem;
                    border-radius: 0.25rem;
                    font-size: 0.875rem;
                    font-family: 'Courier New', Courier, monospace;
                    display: inline;
                    margin: 0;
                    line-height: inherit;
                    vertical-align: baseline;
                  }
                  .content-display pre {
                    margin: 1rem 0;
                  }
                  .content-display pre code {
                    display: block;
                    background-color: rgba(0, 0, 0, 0.05);
                    padding: 1rem;
                    border-radius: 0.5rem;
                    overflow-x: auto;
                    font-size: 0.875rem;
                    font-family: 'Courier New', Courier, monospace;
                  }
                  .content-display a {
                    color: black;
                    text-decoration: none;
                  }
                  .content-display a:hover {
                    text-decoration: underline;
                  }
                  .content-display strong {
                    font-weight: 700;
                  }
                  .content-display em {
                    font-style: italic;
                  }
                  .content-display table {
                    width: 100%;
                    border-collapse: collapse;
                    margin: 1rem 0;
                    overflow-x: auto;
                    display: block;
                  }
                  .content-display thead {
                    background-color: rgba(0, 0, 0, 0.05);
                  }
                  .content-display tbody {
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                  }
                  .content-display tr {
                    border-bottom: 1px solid rgba(0, 0, 0, 0.1);
                  }
                  .content-display th {
                    padding: 0.5rem 1rem;
                    text-align: left;
                    font-size: 0.875rem;
                    font-weight: 600;
                  }
                  .content-display td {
                    padding: 0.5rem 1rem;
                    font-size: 0.875rem;
                  }
                  .content-display img {
                    max-width: 50%;
                    max-height: 400px;
                    width: auto;
                    height: auto;
                    object-fit: contain;
                    border-radius: 0.5rem;
                    margin: 1rem 0;
                    display: block;
                  }
                  .content-display hr {
                    border: none;
                    border-top: 1px solid rgba(0, 0, 0, 0.1);
                    margin: 1rem 0;
                  }
                `}</style>
                <div className="content-display" dangerouslySetInnerHTML={{
                  __html: marked.parse(contentPages[contentPage - 1] || '')
                }} />
              </div>

              {/* Modern pagination for long content */}
              {contentPages.length > 1 && (
                <div className="modern-pagination-container">
                  <div className="pagination-info">
                    <span className="page-indicator">
                      {contentPage} of {contentPages.length}
                    </span>
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-control"
                      onClick={() => setContentPage(prev => Math.max(prev - 1, 1))}
                      disabled={contentPage === 1}
                      aria-label="Previous page"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="m15 18-6-6 6-6" />
                      </svg>
                    </button>

                    <div className="page-numbers">
                      {(() => {
                        const totalPages = contentPages.length;
                        const isMobile = windowWidth < 576;
                        const maxVisible = isMobile ? 3 : 5;

                        let startPage = Math.max(1, contentPage - Math.floor(maxVisible / 2));
                        let endPage = Math.min(totalPages, startPage + maxVisible - 1);

                        if (endPage - startPage + 1 < maxVisible) {
                          startPage = Math.max(1, endPage - maxVisible + 1);
                        }

                        return Array.from({ length: endPage - startPage + 1 }, (_, i) => {
                          const pageNum = startPage + i;
                          return (
                            <button
                              key={pageNum}
                              className={`page-number ${contentPage === pageNum ? 'active' : ''}`}
                              onClick={() => setContentPage(pageNum)}
                            >
                              {pageNum}
                            </button>
                          );
                        });
                      })()}
                    </div>

                    <button
                      className="pagination-control"
                      onClick={() => setContentPage(prev => Math.min(prev + 1, contentPages.length))}
                      disabled={contentPage === contentPages.length}
                      aria-label="Next page"
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="m9 18 6-6-6-6" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* Confirmation modal */}
      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        contentClassName="border-0 shadow-lg"
      >
        <Modal.Header closeButton className="border-bottom-0 pb-0">
          <Modal.Title className="text-danger fw-bold">
            {t('dashboard.training.traininglist.confirmDeletion')}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="pt-3">
          <div className="d-flex">
            <div className="me-3 text-danger">
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
              </svg>
            </div>
            <p className="mb-0">{getConfirmMessage()}</p>
          </div>
        </Modal.Body>
        <Modal.Footer className="border-top-0">
          <CancelButton
            onClick={() => setShowConfirmModal(false)}
            className="me-2"
          >
            {t('dashboard.training.traininglist.cancelButton')}
          </CancelButton>
          <DangerButton
            onClick={handleConfirm}
          >
            <span className="d-flex align-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="me-2" viewBox="0 0 16 16">
                <path d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z" />
                <path fillRule="evenodd" d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z" />
              </svg>
              {t('dashboard.training.traininglist.deleteButton')}
            </span>
          </DangerButton>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default TrainingList;
